import * as UI from "@iyk/music-ui"
import { AppLink } from "@iyk/react-router/client"
import { AppResponse, HttpRequest, LocaleMiddleware } from "@iyk/react-router/server"
import { Effect, Match } from "effect"
import { AppRoutes } from "../AppRoutes.ts"
import { Meta } from "../ui/components/Meta.tsx"
import { Shell } from "../ui/components/shell/Shell.tsx"
import { BffUnlock } from "../unlocks/BffUnlock.ts"
import { UnlockedUnlock } from "../unlocks/ui/components/UnlockedUnlock.tsx"
import { BffUser } from "../users/BffUser.ts"
import { SessionHandler } from "../users/SessionHandler.ts"
import type { Route } from "./+types/HomeRoute.ts"
import { MarketingLandingPage } from "./MarketingLandingPage.tsx"

export const loader = SessionHandler.MaybeKnown.use(LocaleMiddleware())
  .loader("home/HomeRoute")(HttpRequest.get.build())
  .handle((_, { session, capabilities, config: { studioUrl }, locale }) =>
    Effect.gen(function* () {
      const user = BffUser.fromSession(session)

      const unlocks = yield* Match.valueTags(session, {
        Guest: () => Effect.succeed([]),
        Known: ({ token }) =>
          capabilities.unlocks.findByUser(token).pipe(
            Effect.map((unlocks) => unlocks.map(BffUnlock.Unlocked.fromDomain)),
            Effect.catchTag("Unauthenticated", () => Effect.succeed([]))
          )
      })

      const joinWaitlistUrl = new URL("/music", studioUrl)
      joinWaitlistUrl.searchParams.set("waitlist", "open")

      return AppResponse.Json({ user, unlocks, locale, joinWaitlistUrl: joinWaitlistUrl.toString() })
    })
  )

export default function Home({ loaderData }: Route.ComponentProps) {
  const isSignedIn = Boolean(loaderData.user)

  if (isSignedIn) {
    return (
      <Shell.App
        headerPosition="sticky"
        user={loaderData.user}
        header={<Shell.Title title="Home" font="regola" route={AppRoutes.home} />}
      >
        <Meta />
        <UI.FeedMediaController.Provider>
          <div className="grow flex flex-col">
            {loaderData.unlocks.length > 0 ? (
              loaderData.unlocks.map((unlock) => (
                <UnlockedUnlock key={unlock.value.id} unlock={unlock} locale={loaderData.locale} />
              ))
            ) : (
              <div className="grow flex items-center justify-center flex-col gap-4">
                <div className="bg-gray-3 size-9 flex items-center justify-center rounded-md">
                  <UI.MusicIcon.Stack kind="outline" />
                </div>
                <div className="text-center flex flex-col gap-1">
                  <h2 className="text-base">No unlocks yet</h2>
                  <p className="text-gray-11 text-sm">
                    Engage with artists to begin
                    <br /> collecting unlocks
                  </p>
                </div>
                <AppLink
                  className={UI.classForButton({ size: "sm", kind: "shaded" })}
                  to={AppRoutes.myWorlds}
                >
                  Explore worlds
                </AppLink>
              </div>
            )}
          </div>
        </UI.FeedMediaController.Provider>
      </Shell.App>
    )
  }

  return <MarketingLandingPage joinWaitlistUrl={loaderData.joinWaitlistUrl} />
}
